import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button, SliderProps, useTheme } from '@mui/material';

import clsx from 'clsx';
import _isArray from 'lodash/isArray';
import styled from 'styled-components';

import { Constants, scaledRangeChartValues } from '@arrived/common';
import { useFormatPercent } from '@arrived/hooks';

import { Slider, Mark } from '../../../Slider';
import { CalculatorCardStat } from './ROICalculatorPage/CalculatorCardStat';
import { ROICard } from './ROICard';

import { useIsMobile } from '../../../hooks';
import { ArrivedTooltip } from '../../../tooltips';
import { StyledControlContainer, StyledSliderContainer } from './components';

const MIN_SLIDER_VALUE = 0;
const MAX_SLIDER_VALUE = 0.08;
const bounds = { min: MIN_SLIDER_VALUE, max: MAX_SLIDER_VALUE };
interface RentalCardProps {
  currentDividendRate: number;
  averageDividendRate: number;
  dividendRate: number;
  setDividendRate: (dividendRate: number) => void;
  showCharts: boolean;
  marketName: string;
  toggleShowCharts: () => void;
  isLTR: boolean;
}

const StyledRentalGrowthCard = styled(ROICard)`
  .calculator-inner-title {
    height: 250px;

    &.button-margin-offset {
      margin-bottom: 68px;
    }

    .rental-card-stat {
      margin-top: 31px;
    }
  }
`;

export const RentalCard = ({
  averageDividendRate,
  currentDividendRate,
  dividendRate,
  setDividendRate,
  showCharts,
  marketName,
  toggleShowCharts,
  isLTR,
}: RentalCardProps) => {
  const isMobile = useIsMobile();
  const formatPercent = useFormatPercent();
  const { palette } = useTheme();

  const handleOnChange: SliderProps['onChange'] = useCallback(
    (_: Event, val: number | number[]) => {
      if (_isArray(val)) {
        return;
      }
      setDividendRate(val);
    },
    [setDividendRate]
  );

  return (
    <StyledRentalGrowthCard
      titleNumber={2}
      title={<FormattedMessage id="calculator-page-rental-income" defaultMessage="Rental Income" />}
    >
      <div className={clsx('calculator-inner-title', { 'button-margin-offset': !isLTR && !isMobile })}>
        <CalculatorCardStat
          className="rental-card-stat"
          label={<FormattedMessage id="roi-rental-dividend-yield" defaultMessage="Rental Dividend Yield" />}
          labelTypography={{ variant: 'body1.semibold' }}
          percentage={dividendRate}
          PercentTypographyProps={{
            color: ({ palette }) => palette.primary.main,
            variant: 'h5',
            fontWeight: 'semibold',
          }}
        />
        <StyledControlContainer>
          <StyledSliderContainer>
            {isLTR && (
              <Mark
                label={
                  <ArrivedTooltip
                    disableUnderline
                    titleVariant="body2"
                    title={<FormattedMessage id="historical-average" defaultMessage="Historical Average" />}
                  >
                    <FormattedMessage
                      id="historical-average-rent-tooltip"
                      defaultMessage={`The average annualized dividend yield paid by Arrived properties in the {market}
                     market since Arrived has offered properties in the market.`}
                      values={{
                        market: marketName,
                      }}
                    />
                  </ArrivedTooltip>
                }
                percent={formatPercent(averageDividendRate)}
                $start={scaledRangeChartValues(averageDividendRate, bounds)}
                $position="bottom"
              />
            )}
            {currentDividendRate != null && (
              <Mark
                label={
                  <ArrivedTooltip
                    disableUnderline
                    titleVariant="body2"
                    title={<FormattedMessage id="current" defaultMessage="Current" />}
                  >
                    <FormattedMessage
                      id="current-rent-tooltip"
                      defaultMessage={`The current dividend yield is the return on investment based on either the first dividend payment
                       for the property or the most recent dividend payment if there have been more than one. The dividend percentage is
                        calculated by dividing the dividend payment by the raise amount and annualizing that figure.`}
                    />
                  </ArrivedTooltip>
                }
                percent={formatPercent(currentDividendRate)}
                $start={scaledRangeChartValues(currentDividendRate, bounds)}
                $position="top"
              />
            )}
            <Slider
              className="card-slider"
              $color={palette.primary.main}
              min={MIN_SLIDER_VALUE}
              max={MAX_SLIDER_VALUE}
              step={0.001}
              value={dividendRate}
              defaultValue={dividendRate}
              onChange={handleOnChange}
            />
          </StyledSliderContainer>
        </StyledControlContainer>
      </div>
      {isLTR && (
        <Button
          variant="text"
          color="primary"
          size="small"
          className="calculator-view-charts-button"
          onClick={toggleShowCharts}
        >
          {showCharts ? (
            <FormattedMessage id="calculator-view-charts-hide" defaultMessage="Hide Charts" />
          ) : (
            <FormattedMessage id="calculator-view-charts-show" defaultMessage="View Charts" />
          )}
        </Button>
      )}
    </StyledRentalGrowthCard>
  );
};
